
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Users, Search, X, LogOut, Shield, Loader } from 'lucide-react';
import { User, Conversation } from './types';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, auth } from './firebaseConfig';
import { addGroupMembers, removeGroupMember, leaveGroup } from './services/chatService';
import { searchUsersByName, getUserPublicProfile, UserPublicProfile } from './services/userService';

export const GroupInfoView = ({
  user,
  authReady,
  conversationId,
  onBack
}: {
  user: User;
  authReady: boolean;
  conversationId: string;
  onBack: () => void;
}) => {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [memberProfiles, setMemberProfiles] = useState<Record<string, UserPublicProfile>>({});
  const [showAddMember, setShowAddMember] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UserPublicProfile[]>([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Subscribe to conversation
  useEffect(() => {
    // 🔑 CRITICAL: Block ALL snapshots until auth is ready
    if (!authReady) return;
    
    // 🔒 CRITICAL FIX: Verify user is authenticated BEFORE creating listener
    const authUid = auth.currentUser?.uid;
    if (!authUid) {
      if (import.meta.env.DEV) {
        console.warn('[GROUP_INFO] onSnapshot blocked: no authenticated user');
      }
      setLoading(false);
      setError('Not authenticated');
      return;
    }

    const conversationRef = doc(db, 'conversations', conversationId);
    
    // CRITICAL: Initialize as null, assign only if onSnapshot returns a function
    let unsubscribe: (() => void) | null = null;
    
    try {
      const snapshotResult = onSnapshot(conversationRef, (snap) => {
        if (snap.exists()) {
          const data = { id: snap.id, ...snap.data() } as Conversation;
          setConversation(data);
          setLoading(false);

          // Load member profiles
          const profilePromises = data.participants.map(uid =>
            getUserPublicProfile(uid).then(profile => {
              setMemberProfiles(prev => ({ ...prev, [uid]: profile }));
            })
          );
          Promise.all(profilePromises);
        } else {
          setLoading(false);
        }
      });
      if (typeof snapshotResult === 'function') {
        unsubscribe = snapshotResult;
      }
    } catch (initError: any) {
      if (import.meta.env.DEV) {
        console.error('[GROUP_INFO] Error initializing conversation snapshot listener:', initError);
      }
    }

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [authReady, conversationId]);

  // Search users for adding
  useEffect(() => {
    if (!showAddMember || !searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchUsersByName(searchTerm, user.id);
        // Filter out existing members
        const existingUids = conversation?.participants || [];
        const filtered = results.filter(r => !existingUids.includes(r.uid));
        setSearchResults(filtered);
      } catch (err) {
        console.error('[GROUP_INFO] Search error:', err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, showAddMember, conversation, user.id]);

  const isAdmin = conversation?.adminsMap?.[user.id] || false;
  const isGroup = conversation?.type === 'group';

  const handleAddMember = async (member: UserPublicProfile) => {
    if (!conversation || !isAdmin) return;

    setError('');
    try {
      await addGroupMembers(conversationId, user.id, [member.uid]);
      setSearchTerm('');
      setSearchResults([]);
      setShowAddMember(false);
    } catch (err: any) {
      setError(err.message || 'Errore nell\'aggiunta del membro');
    }
  };

  const handleRemoveMember = async (memberUid: string) => {
    if (!conversation || !isAdmin) return;

    if (memberUid === user.id) {
      setError('Usa "Lascia gruppo" per uscire');
      return;
    }

    setError('');
    try {
      await removeGroupMember(conversationId, user.id, memberUid);
    } catch (err: any) {
      setError(err.message || 'Errore nella rimozione del membro');
    }
  };

  const handleLeaveGroup = async () => {
    if (!conversation) return;

    if (!confirm('Sei sicuro di voler lasciare questo gruppo?')) {
      return;
    }

    setError('');
    try {
      await leaveGroup(conversationId, user.id);
      onBack();
    } catch (err: any) {
      setError(err.message || 'Errore nell\'uscita dal gruppo');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Loader className="animate-spin text-cyan-500 w-12 h-12" />
      </div>
    );
  }

  if (!conversation || !isGroup) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <p className="text-slate-500">Gruppo non trovato</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0a0a0f]/95 backdrop-blur-xl border-b border-slate-800">
        <div className="flex items-center gap-4 p-4 md:p-6">
          <button
            onClick={onBack}
            className="p-2 hover:bg-slate-800 rounded-xl text-slate-400 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-3">
            <Users className="text-cyan-400" size={24} />
            <h1 className="text-2xl font-black text-white uppercase tracking-tight">Info Gruppo</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 max-w-2xl mx-auto w-full">
        {error && (
          <div className="bg-red-500/10 text-red-400 p-4 rounded-2xl border border-red-500/20 text-sm">
            {error}
          </div>
        )}

        {/* Group Info */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-4">
            {conversation.photoURL ? (
              <img
                src={conversation.photoURL}
                alt={conversation.title}
                className="w-20 h-20 rounded-2xl object-cover border-2 border-slate-800"
              />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-slate-800 flex items-center justify-center border-2 border-slate-700">
                <Users className="text-slate-500" size={32} />
              </div>
            )}
            <div className="flex-1">
              <h2 className="text-xl font-black text-white mb-1">{conversation.title}</h2>
              {conversation.description && (
                <p className="text-sm text-slate-400">{conversation.description}</p>
              )}
              <p className="text-xs text-slate-500 mt-2">
                {conversation.participants.length} {t?.chat_group_members?.toLowerCase() || 'members'}
              </p>
            </div>
          </div>
        </div>

        {/* Members List */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest">
              {t?.chat_group_members || 'Members'} ({conversation.participants.length})
            </h3>
            {isAdmin && (
              <button
                onClick={() => setShowAddMember(!showAddMember)}
                className="text-xs font-black text-cyan-400 hover:text-cyan-300 uppercase tracking-wider"
              >
                {showAddMember ? 'Annulla' : '+ Aggiungi'}
              </button>
            )}
          </div>

          {/* Add Member Search */}
          {showAddMember && isAdmin && (
            <div className="space-y-2 pb-4 border-b border-slate-800">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t?.chat_search_users || t?.chat_group_search_placeholder || "Cerca utenti..."}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl pl-12 pr-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition-colors text-sm"
                />
                {searching && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <Loader className="animate-spin text-slate-500" size={16} />
                  </div>
                )}
              </div>

              {searchTerm.trim() && searchResults.length > 0 && (
                <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
                  {searchResults.map((result) => (
                    <button
                      key={result.uid}
                      onClick={() => handleAddMember(result)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-slate-700 transition-colors text-left"
                    >
                      <img
                        src={result.photoURL}
                        alt={result.displayName}
                        className="w-8 h-8 rounded-lg object-cover"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-bold text-white">{result.displayName}</p>
                        {result.username && (
                          <p className="text-xs text-slate-500">@{result.username}</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Members List */}
          <div className="space-y-2">
            {conversation.participants.map((uid) => {
              const profile = memberProfiles[uid] || {
                uid,
                displayName: 'Utente',
                photoURL: `https://api.dicebear.com/7.x/avataaars/svg?seed=${uid}`,
                username: undefined
              };
              const isMemberAdmin = conversation.adminsMap?.[uid] || false;
              const isCurrentUser = uid === user.id;

              return (
                <div
                  key={uid}
                  className="flex items-center gap-3 p-3 bg-slate-800 rounded-xl"
                >
                  <img
                    src={profile.photoURL}
                    alt={profile.displayName}
                    className="w-10 h-10 rounded-xl object-cover"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold text-white">{profile.displayName}</p>
                      {isMemberAdmin && (
                        <Shield className="text-cyan-400" size={14} />
                      )}
                      {isCurrentUser && (
                        <span className="text-xs text-slate-500">(Tu)</span>
                      )}
                    </div>
                    {profile.username && (
                      <p className="text-xs text-slate-500">@{profile.username}</p>
                    )}
                  </div>
                  {isAdmin && !isCurrentUser && (
                    <button
                      onClick={() => handleRemoveMember(uid)}
                      className="p-2 hover:bg-slate-700 rounded-lg transition-colors text-red-400"
                      title="Rimuovi membro"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Leave Group Button */}
        <button
          onClick={handleLeaveGroup}
          className="w-full bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 font-black py-4 rounded-xl transition-all flex items-center justify-center gap-2"
        >
          <LogOut size={20} />
          <span>Lascia Gruppo</span>
        </button>
      </div>
    </div>
  );
};

